/**
 * Custom-column cell for `status` columns. Click opens a popover with the
 * per-column options (configured by the user via the column header menu).
 * Selecting an option persists its id; selecting "Clear", where the column
 * allows it, removes the value.
 */
import { badgeVariants, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { statusChipColors } from '../../lib/status-chip-colors';
import type { StatusOption } from '../../lib/useTrackersBoard';
import { useTranslation } from 'react-i18next';

interface StatusCustomCellProps {
  value: string | undefined;
  onChange: (next: string | null) => void;
  /** Per-column status options. May be empty until the column is configured. */
  options: StatusOption[];
  /**
   * Whether the popover offers Clear. False for a platform field the record
   * cannot hold empty, where the write comes back a server error and the cell
   * reverts, so the item is a promise the board cannot keep.
   */
  clearable?: boolean;
}

export function StatusCustomCell({
  value,
  onChange,
  options,
  clearable = true,
}: StatusCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);

  const triggerStyle: React.CSSProperties = current ? statusChipColors(current.color) : {};
  const triggerLabel = current?.label ?? '—';

  // Badge is a `<span>` with no `asChild`, so the chip borrows its recipe
  // instead of its element: the geometry stays the design system's, and the
  // trigger stays a button. Unset falls back to the muted token pair.
  // The recipe's own `font-medium` is overridden: a status sits in a grid of
  // them, and at that density the bolder weight reads as emphasis the value
  // does not carry.
  const chipClasses = badgeVariants({
    roundness: 'full',
    className:
      'max-w-full overflow-hidden font-normal transition-opacity hover:opacity-80 focus-visible:focus-ring',
  });

  // The same chip one step smaller, so the menu previews what the row will show.
  const optionChipClasses = badgeVariants({
    size: 'sm',
    roundness: 'full',
    className: 'w-full justify-between font-normal transition-opacity hover:opacity-80',
  });

  const clearChipClasses = badgeVariants({
    size: 'sm',
    roundness: 'full',
    className:
      'mt-1 w-full justify-start bg-muted font-normal text-muted-foreground transition-opacity hover:opacity-80',
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="status-chip"
          onClick={(e) => e.stopPropagation()}
          className={current ? chipClasses : `${chipClasses} bg-muted text-muted-foreground`}
          style={triggerStyle}
        >
          {/*
            Truncates on its own span, never on the chip: the chip is a flex
            box, and `text-overflow` is ignored on one, so a label styled there
            clips with no ellipsis at all.
          */}
          <span className="truncate" title={triggerLabel}>
            {triggerLabel}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col gap-1">
          {options.length === 0 ? (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              {t('trackersBoard.statusCustomCell.noOptionsYetOpenConfigure')}
            </div>
          ) : (
            options.map((option) => {
              const isCurrent = option.id === value;
              return (
                <button
                  key={option.id}
                  type="button"
                  data-slot="status-chip"
                  onClick={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                  className={optionChipClasses}
                  style={statusChipColors(option.color)}
                >
                  <span className="truncate">{option.label}</span>
                  {isCurrent ? <Check className="size-3 shrink-0" aria-hidden /> : null}
                </button>
              );
            })
          )}
          {value && clearable ? (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className={clearChipClasses}
            >
              <X className="size-3" aria-hidden />
              <span>{t('trackersBoard.statusCustomCell.clear')}</span>
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
